// The Tour Creator's stop bank — the reusable library of every stop written —
// as pure functions, so src/engtour.bank.test.js pins the real code rather
// than a mirror of it.
//
// A bank entry holds a stop's content (everything except the tour-specific
// What's Next) plus two flags that belong to the PLACE, not to any tour:
//   freeRoam  the Free Roam star — shown on the visitor's Free Roam map
//   primary   a "primary landmark" — its star stays visible zoomed out
// Those two are BANK-ONLY. Tour stops used to carry copies of them (a stop
// inserted from the bank, or saved from the bank editor, took the whole
// entry), and `buildStopBank` let the stop's copy win on every save — so
// switching a star off in the FREE ROAM tab came back on at Save for any place
// that was also a tour stop, and a stop holding a stale `false` silently
// un-starred its place. Reported 2026-10-13 as "some of the stars were turned
// off". Now the bank's value wins whenever the bank has the entry, and the
// copies are stripped from stops as they are written.

export function normalizeBuilding(b) {
  if (b == null) return null;
  return String(b).toLowerCase().replace(/^eitc[\s_-]*/, '').trim();
}

// A hand-placed spot has no room number, so building+room would key every spot
// in a building to the same slot and silently collapse them into one. Those are
// identified by the nav node they were pinned to instead. Stops made from real
// rooms keep the original key exactly, so existing data is untouched.
export const bankKey = (s) => (s?.nodeId
  ? `node::${s.nodeId}`
  : `${normalizeBuilding(s.building)}::${String(s.room || "").toLowerCase()}`);

export const BANK_ONLY_FLAGS = ["freeRoam", "primary"];

/** A stop as a tour should hold it: without copies of the bank-only flags. */
export function stripBankFlags(stop) {
  if (!stop || typeof stop !== "object") return stop;
  const { freeRoam, primary, ...rest } = stop; // eslint-disable-line no-unused-vars
  return rest;
}

// Keep any existing bank entries, then upsert current stops (newest content
// wins) while keeping the bank-only flags from the bank. `room != null` was the
// test for "a real entry"; a hand-placed spot has no room and would be dropped
// from the bank on the next save, so a nav node id counts as identity too.
export function buildStopBank(td) {
  const bank = new Map();
  (td.stopBank || []).forEach(s => { if (s && (s.room != null || s.nodeId)) bank.set(bankKey(s), s); });
  (td.tours || []).forEach(t => (t.stops || []).forEach(s => {
    const { guidance, ...rest } = s; // eslint-disable-line no-unused-vars
    const key = bankKey(rest);
    const prev = bank.get(key);
    const entry = { ...stripBankFlags(rest), freeRoam: prev ? !!prev.freeRoam : !!rest.freeRoam };
    const primary = prev ? prev.primary : rest.primary;
    if (primary !== undefined) entry.primary = !!primary;
    bank.set(key, entry);
  }));
  return Array.from(bank.values());
}

// Migration from the July 2026 "Free Roam = starred bank places" change: fold the
// legacy `freeRoamLocations` list into the bank as starred entries. Mutates `data`.
//
// ⚠️ It used to run on EVERY load and never clear the list, so the old list's eight
// places — including the duplicates "E2-UMES Lounge", "E2-433", "Engineering
// Library" and "Makerspace" (rooms typed as names) — were re-starred, with their
// old primary flags, each time the creator opened, and published by the next Save.
// An editor's "turned those off" never stuck, and a deleted place came back.
// Reported 2026-10-13; fixed 2026-10-15. Pinned by src/engtour.intro.test.js.
export function migrateFreeRoamToBank(data) {
  const legacy = Array.isArray(data.freeRoamLocations) ? data.freeRoamLocations : [];
  if (!legacy.length) return;
  // Only a document from before the bank model — nothing starred yet — is folded in.
  // After that the bank's stars are the truth, and the old list is dropped (the next
  // Save writes it as []), so it can never re-star or resurrect a place again.
  if ((data.stopBank || []).some(s => s && s.freeRoam)) { data.freeRoamLocations = []; return; }
  const bank = new Map((data.stopBank || []).map(s => [bankKey(s), s]));
  for (const loc of legacy) {
    if (loc.room == null) continue;
    const key = bankKey(loc);
    const prev = bank.get(key) || {};
    bank.set(key, {
      building: loc.building, room: loc.room,
      title: prev.title || loc.title || "",
      label: prev.label || loc.label || "",
      subtitle: prev.subtitle || loc.subtitle || "",
      text: prev.text || loc.description || "",
      images: prev.images || loc.images || [],
      videoUrl: prev.videoUrl || loc.videoUrl || "",
      departments: (prev.departments && prev.departments.length) ? prev.departments : (loc.departments || []),
      icon: loc.icon || prev.icon || "📍",
      primary: loc.primary ?? prev.primary ?? false,
      freeRoam: true,
      ...(prev.quote ? { quote: prev.quote } : {}),
    });
  }
  data.stopBank = Array.from(bank.values());
  data.freeRoamLocations = [];
}

// ── Free Roam visibility: one three-way setting ─────────────────────────────
// What editors pick, mapped onto the two stored flags the viewer already reads
// (EngTourPage draws a star when `freeRoam`, and below `primaryZoom` only when
// `primary`). "Primary landmark" read as unclear, so the creator shows one button
// that cycles through these three instead of SHOW + Primary (2026-10-15).
export const FREE_ROAM_MODES = [
  { mode: 'off', label: "Don't show", hint: 'Not on the visitor\'s Free Roam map.' },
  { mode: 'always', label: 'Show always', hint: 'Its star is on the visitor\'s map from the start, even zoomed out.' },
  { mode: 'zoomed', label: 'Show zoomed', hint: 'Its star appears once the visitor zooms in (Free Roam opens zoomed out).' },
];

export function freeRoamMode(s) {
  if (!s || !s.freeRoam) return 'off';
  return s.primary ? 'always' : 'zoomed';
}

/** The fields to set for a mode. */
export function freeRoamPatch(mode) {
  if (mode === 'always') return { freeRoam: true, primary: true };
  if (mode === 'zoomed') return { freeRoam: true, primary: false };
  return { freeRoam: false };
}

/** The next mode when the button is clicked: Don't show → Show always → Show zoomed → … */
export function nextFreeRoamMode(mode) {
  const i = FREE_ROAM_MODES.findIndex(m => m.mode === mode);
  return FREE_ROAM_MODES[(i + 1) % FREE_ROAM_MODES.length].mode;
}
